import { useMemo, useState } from 'react';
import {
  PRODUCT_PROGRESS,
  PRODUCT_PROGRESS_KEYS,
  clampProgress,
  type ProductProgressKey,
} from '@/config/productProgress';
import {
  PRODUCT_STATS,
  PRODUCT_STATS_KEYS,
  clampDownloads,
  normalizeBoolean,
  normalizeRating,
  type ProductStats,
  type ProductStatsKey,
  type ProductStatsMap,
} from '@/config/productStats';
import {
  SPECIAL_DAY_LOGO_OPTIONS,
  type SpecialDayLogoKey,
} from '@/hooks/useSpecialDayLogo';


const MAINTENANCE_ENDPOINT = '/api/admin/maintenance';
const PROGRESS_ENDPOINT = '/api/admin/progress';
const STATS_ENDPOINT = '/api/admin/stats';
const SHOWCASE_ENDPOINT = '/api/admin/showcase';
const BRAND_LOGO_ENDPOINT = '/api/admin/brand-logo';

type MaintenanceState = boolean | null;
type ProductProgressMap = Record<ProductProgressKey, number>;

interface WebsiteShowcaseItem {
  id: string;
  name: string;
  logoUrl: string;
  siteUrl: string;
}

interface BrandLogoState {
  mode: 'automatic' | 'manual';
  logoKey: SpecialDayLogoKey | null;
  logoUrl: string | null;
  activeDate: string | null;
}

interface BrandLogoPayload {
  mode?: string;
  logoKey?: string | null;
  logoUrl?: string | null;
  activeDate?: string | null;
}

interface ApiResponse {
  success?: boolean;
  maintenance?: boolean;
  progress?: Partial<Record<ProductProgressKey, number>>;
  stats?: Partial<Record<ProductStatsKey, Partial<ProductStats>>>;
  items?: WebsiteShowcaseItem[];
  brandLogo?: BrandLogoPayload;
  message?: string;
}

const AUTOMATIC_BRAND_LOGO_STATE: BrandLogoState = {
  mode: 'automatic',
  logoKey: null,
  logoUrl: null,
  activeDate: null,
};

const PRODUCT_LABELS: Record<ProductProgressKey, { name: string; meta: string }> = {
  'defter-kasa': { name: 'Defter Kasa', meta: 'Cari takip uygulaması' },
  'kozmik-gunluk': { name: 'Kozmik Günlük', meta: 'Günlük uygulaması' },
  'anilar-muzesi': { name: 'Anılar Müzesi', meta: 'Arşivlenen oyun' },
};

async function readResponse(response: Response): Promise<ApiResponse> {
  const contentType = response.headers.get('content-type') || '';
  if (contentType.includes('application/json')) {
    return (await response.json().catch(() => ({}))) as ApiResponse;
  }

  const text = await response.text().catch(() => '');
  return text ? { message: text } : {};
}

function normalizeProgress(value: ApiResponse['progress']): ProductProgressMap {
  return {
    'defter-kasa': clampProgress(value?.['defter-kasa'] ?? PRODUCT_PROGRESS['defter-kasa']),
    'kozmik-gunluk': clampProgress(value?.['kozmik-gunluk'] ?? PRODUCT_PROGRESS['kozmik-gunluk']),
    'anilar-muzesi': clampProgress(value?.['anilar-muzesi'] ?? PRODUCT_PROGRESS['anilar-muzesi']),
  };
}

function normalizeStats(value: ApiResponse['stats']): ProductStatsMap {
  return {
    'defter-kasa': {
      downloads: clampDownloads(value?.['defter-kasa']?.downloads ?? PRODUCT_STATS['defter-kasa'].downloads),
      rating: normalizeRating(value?.['defter-kasa']?.rating ?? PRODUCT_STATS['defter-kasa'].rating),
      showDownloadsInApps: normalizeBoolean(value?.['defter-kasa']?.showDownloadsInApps, PRODUCT_STATS['defter-kasa'].showDownloadsInApps),
    },
    'kozmik-gunluk': {
      downloads: clampDownloads(value?.['kozmik-gunluk']?.downloads ?? PRODUCT_STATS['kozmik-gunluk'].downloads),
      rating: normalizeRating(value?.['kozmik-gunluk']?.rating ?? PRODUCT_STATS['kozmik-gunluk'].rating),
      showDownloadsInApps: normalizeBoolean(value?.['kozmik-gunluk']?.showDownloadsInApps, PRODUCT_STATS['kozmik-gunluk'].showDownloadsInApps),
    },
    'anilar-muzesi': {
      downloads: clampDownloads(value?.['anilar-muzesi']?.downloads ?? PRODUCT_STATS['anilar-muzesi'].downloads),
      rating: normalizeRating(value?.['anilar-muzesi']?.rating ?? PRODUCT_STATS['anilar-muzesi'].rating),
      showDownloadsInApps: normalizeBoolean(value?.['anilar-muzesi']?.showDownloadsInApps, PRODUCT_STATS['anilar-muzesi'].showDownloadsInApps),
    },
  };
}

function normalizeShowcaseItems(value: ApiResponse['items']): WebsiteShowcaseItem[] {
  return Array.isArray(value)
    ? value
        .map((item) => ({
          id: String(item.id || `site-${Date.now()}`),
          name: String(item.name || '').trim(),
          logoUrl: String(item.logoUrl || '').trim(),
          siteUrl: String(item.siteUrl || '').trim(),
        }))
        .filter((item) => item.name && item.logoUrl)
    : [];
}

function normalizeBrandLogo(value: ApiResponse['brandLogo']): BrandLogoState {
  const option = SPECIAL_DAY_LOGO_OPTIONS.find((item) => item.key === value?.logoKey);
  const activeDate = typeof value?.activeDate === 'string' ? value.activeDate : null;

  if (value?.mode === 'manual' && option && activeDate) {
    return {
      mode: 'manual',
      logoKey: option.key,
      logoUrl: option.logoUrl,
      activeDate,
    };
  }

  return AUTOMATIC_BRAND_LOGO_STATE;
}

function createShowcaseId() {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }

  return `site-${Date.now()}-${Math.round(Math.random() * 10000)}`;
}

export default function MaintenanceControlPage() {
  const [tokenInput, setTokenInput] = useState('');
  const [activeToken, setActiveToken] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authLoading, setAuthLoading] = useState(false);
  const [maintenance, setMaintenance] = useState<MaintenanceState>(null);
  const [progress, setProgress] = useState<ProductProgressMap>(PRODUCT_PROGRESS);
  const [stats, setStats] = useState<ProductStatsMap>(PRODUCT_STATS);
  const [showcaseItems, setShowcaseItems] = useState<WebsiteShowcaseItem[]>([]);
  const [brandLogo, setBrandLogo] = useState<BrandLogoState>(AUTOMATIC_BRAND_LOGO_STATE);
  const [newCompanyName, setNewCompanyName] = useState('');
  const [newLogoUrl, setNewLogoUrl] = useState('');
  const [newSiteUrl, setNewSiteUrl] = useState('');
  const [maintenanceLoading, setMaintenanceLoading] = useState(false);
  const [progressLoading, setProgressLoading] = useState(false);
  const [statsLoading, setStatsLoading] = useState(false);
  const [showcaseLoading, setShowcaseLoading] = useState(false);
  const [brandLogoLoading, setBrandLogoLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const hasToken = useMemo(() => activeToken.trim().length > 0, [activeToken]);
  const busy = authLoading || maintenanceLoading || progressLoading || statsLoading || showcaseLoading || brandLogoLoading;

  const authHeaders = (token: string) => ({
    'x-maintenance-token': token,
  });

  const fetchMaintenanceStatus = async (token: string) => {
    setMaintenanceLoading(true);
    setError('');

    try {
      const response = await fetch(MAINTENANCE_ENDPOINT, {
        method: 'GET',
        headers: authHeaders(token),
      });
      const data = await readResponse(response);

      if (!response.ok) {
        setMaintenance(null);
        setError(data.message || 'Bakim durumu alinamadi.');
        return;
      }

      setMaintenance(Boolean(data.maintenance));
    } catch {
      setMaintenance(null);
      setError('Bakim servisine baglanilamadi.');
    } finally {
      setMaintenanceLoading(false);
    }
  };

  const fetchProgress = async (token: string) => {
    setProgressLoading(true);
    setError('');

    try {
      const response = await fetch(PROGRESS_ENDPOINT, {
        method: 'GET',
        headers: authHeaders(token),
      });
      const data = await readResponse(response);

      if (!response.ok) {
        setError(data.message || 'Ilerleme degerleri alinamadi.');
        return;
      }

      setProgress(normalizeProgress(data.progress));
    } catch {
      setError('Ilerleme servisine baglanilamadi.');
    } finally {
      setProgressLoading(false);
    }
  };

  const fetchStats = async (token: string) => {
    setStatsLoading(true);
    setError('');

    try {
      const response = await fetch(STATS_ENDPOINT, {
        method: 'GET',
        headers: authHeaders(token),
      });
      const data = await readResponse(response);

      if (!response.ok) {
        setError(data.message || 'Urun istatistikleri alinamadi.');
        return;
      }

      setStats(normalizeStats(data.stats));
    } catch {
      setError('Urun istatistikleri servisine baglanilamadi.');
    } finally {
      setStatsLoading(false);
    }
  };

  const fetchShowcase = async (token: string) => {
    setShowcaseLoading(true);
    setError('');

    try {
      const response = await fetch(SHOWCASE_ENDPOINT, {
        method: 'GET',
        headers: authHeaders(token),
      });
      const data = await readResponse(response);

      if (!response.ok) {
        setError(data.message || 'Teslim edilen siteler alinamadi.');
        return;
      }

      setShowcaseItems(normalizeShowcaseItems(data.items));
    } catch {
      setError('Teslim edilen siteler servisine baglanilamadi.');
    } finally {
      setShowcaseLoading(false);
    }
  };

  const fetchBrandLogo = async (token: string) => {
    setBrandLogoLoading(true);
    setError('');

    try {
      const response = await fetch(BRAND_LOGO_ENDPOINT, {
        method: 'GET',
        headers: authHeaders(token),
      });
      const data = await readResponse(response);

      if (!response.ok) {
        setError(data.message || 'Logo modu alinamadi.');
        return;
      }

      setBrandLogo(normalizeBrandLogo(data.brandLogo));
    } catch {
      setError('Logo servisine baglanilamadi.');
    } finally {
      setBrandLogoLoading(false);
    }
  };

  const refreshAll = async (token: string) => {
    await Promise.all([
      fetchMaintenanceStatus(token),
      fetchProgress(token),
      fetchStats(token),
      fetchShowcase(token),
      fetchBrandLogo(token),
    ]);
    setMessage('Panel guncellendi.');
  };

  const updateMaintenance = async (nextState: boolean) => {
    if (!hasToken) {
      setError('Once token gir ve kaydet.');
      return;
    }

    setMaintenanceLoading(true);
    setError('');

    try {
      const response = await fetch(MAINTENANCE_ENDPOINT, {
        method: 'POST',
        headers: {
          ...authHeaders(activeToken),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ enabled: nextState }),
      });
      const data = await readResponse(response);

      if (!response.ok) {
        setError(data.message || 'Bakim modu guncellenemedi.');
        return;
      }

      setMaintenance(Boolean(data.maintenance));
      setMessage(`Bakim modu ${data.maintenance ? 'ACILDI' : 'KAPATILDI'}.`);
    } catch {
      setError('Bakim modu guncellenirken baglanti hatasi olustu.');
    } finally {
      setMaintenanceLoading(false);
    }
  };

  const updateBrandLogo = async (logoKey: SpecialDayLogoKey | null) => {
    if (!hasToken) {
      setError('Once token gir ve kaydet.');
      return;
    }

    setBrandLogoLoading(true);
    setError('');

    try {
      const response = await fetch(BRAND_LOGO_ENDPOINT, {
        method: 'POST',
        headers: {
          ...authHeaders(activeToken),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ logoKey }),
      });
      const data = await readResponse(response);

      if (!response.ok) {
        setError(data.message || 'Logo modu guncellenemedi.');
        return;
      }

      const nextState = normalizeBrandLogo(data.brandLogo);
      setBrandLogo(nextState);
      setMessage(
        nextState.mode === 'manual'
          ? 'Özel gün logosu canlıya alındı. Türkiye saatiyle gece 00:00’da otomatik mod geri dönecek.'
          : 'Logo tekrar otomatik takvim moduna alındı.'
      );
    } catch {
      setError('Logo modu guncellenirken baglanti hatasi olustu.');
    } finally {
      setBrandLogoLoading(false);
    }
  };

  const saveProgress = async () => {
    if (!hasToken) {
      setError('Once token gir ve kaydet.');
      return;
    }

    setProgressLoading(true);
    setError('');

    try {
      const response = await fetch(PROGRESS_ENDPOINT, {
        method: 'POST',
        headers: {
          ...authHeaders(activeToken),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ progress }),
      });
      const data = await readResponse(response);

      if (!response.ok) {
        setError(data.message || 'Ilerleme degerleri kaydedilemedi.');
        return;
      }

      setProgress(normalizeProgress(data.progress));
      setMessage('İlerleme barları kaydedildi. Site bu degerleri kullanacak.');
    } catch {
      setError('Ilerleme degerleri kaydedilirken baglanti hatasi olustu.');
    } finally {
      setProgressLoading(false);
    }
  };

  const saveStats = async () => {
    if (!hasToken) {
      setError('Once token gir ve kaydet.');
      return;
    }

    setStatsLoading(true);
    setError('');

    try {
      const response = await fetch(STATS_ENDPOINT, {
        method: 'POST',
        headers: {
          ...authHeaders(activeToken),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ stats }),
      });
      const data = await readResponse(response);

      if (!response.ok) {
        setError(data.message || 'Urun istatistikleri kaydedilemedi.');
        return;
      }

      setStats(normalizeStats(data.stats));
      setMessage('Ürün istatistikleri kaydedildi, Site bu değerleri kullanacak');
    } catch {
      setError('Urun istatistikleri kaydedilirken baglanti hatasi olustu.');
    } finally {
      setStatsLoading(false);
    }
  };

  const saveShowcase = async () => {
    if (!hasToken) {
      setError('Once token gir ve kaydet.');
      return;
    }

    setShowcaseLoading(true);
    setError('');

    try {
      const response = await fetch(SHOWCASE_ENDPOINT, {
        method: 'POST',
        headers: {
          ...authHeaders(activeToken),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ items: showcaseItems }),
      });
      const data = await readResponse(response);

      if (!response.ok) {
        setError(data.message || 'Teslim edilen siteler kaydedilemedi.');
        return;
      }

      setShowcaseItems(normalizeShowcaseItems(data.items));
      setMessage('Teslim edilen siteler kaydedildi.');
    } catch {
      setError('Teslim edilen siteler kaydedilirken baglanti hatasi olustu.');
    } finally {
      setShowcaseLoading(false);
    }
  };

  const handleLogin = async () => {
    const normalized = tokenInput.trim();
    if (!normalized) {
      setError('Token boş olamaz.');
      return;
    }

    setAuthLoading(true);
    setError('');

    try {
      const response = await fetch(MAINTENANCE_ENDPOINT, {
        method: 'GET',
        headers: authHeaders(normalized),
      });
      const data = await readResponse(response);

      if (!response.ok) {
        setError(response.status === 401 ? 'Token hatalı.' : data.message || 'Giriş doğrulanamadı.');
        return;
      }

      window.localStorage.removeItem('gsoft_maintenance_admin_token');
      setActiveToken(normalized);
      setMaintenance(Boolean(data.maintenance));

      await Promise.all([
        fetchProgress(normalized),
        fetchStats(normalized),
        fetchShowcase(normalized),
        fetchBrandLogo(normalized),
      ]);

      setTokenInput('');
      setIsAuthenticated(true);
      setMessage('Giriş başarılı.');
    } catch {
      setError('Giriş servisine bağlanılamadı.');
    } finally {
      setAuthLoading(false);
    }
  };

  const handleLogout = () => {
    window.localStorage.removeItem('gsoft_maintenance_admin_token');
    setIsAuthenticated(false);
    setTokenInput('');
    setActiveToken('');
    setMaintenance(null);
    setProgress(PRODUCT_PROGRESS);
    setStats(PRODUCT_STATS);
    setShowcaseItems([]);
    setBrandLogo(AUTOMATIC_BRAND_LOGO_STATE);
    setNewCompanyName('');
    setNewLogoUrl('');
    setNewSiteUrl('');
    setError('');
    setMessage('');
  };

  const handleProgressChange = (key: ProductProgressKey, value: number) => {
    setProgress((current) => ({
      ...current,
      [key]: clampProgress(value),
    }));
  };

  const handleStatsChange = (key: ProductStatsKey, patch: Partial<ProductStats>) => {
    setStats((current) => ({
      ...current,
      [key]: {
        ...current[key],
        ...patch,
      },
    }));
  };

  const handleToggle = () => {
    if (maintenance == null) {
      setError('Once durum bilgisini cek.');
      return;
    }
    void updateMaintenance(!maintenance);
  };

  const handleAddShowcaseItem = () => {
    const name = newCompanyName.trim();
    const logoUrl = newLogoUrl.trim();
    const siteUrl = newSiteUrl.trim();

    if (!name || !logoUrl || !siteUrl) {
      setError('Firma adi, logo yolu ve site linki gerekli.');
      return;
    }

    setShowcaseItems((current) => [
      ...current,
      {
        id: createShowcaseId(),
        name,
        logoUrl,
        siteUrl,
      },
    ]);
    setNewCompanyName('');
    setNewLogoUrl('');
    setNewSiteUrl('');
    setError('');
    setMessage('Firma listeye eklendi. Canliya almak icin kaydet.');
  };

  const handleUpdateShowcaseItem = (id: string, patch: Partial<WebsiteShowcaseItem>) => {
    setShowcaseItems((current) =>
      current.map((item) => (item.id === id ? { ...item, ...patch } : item))
    );
  };

  const handleRemoveShowcaseItem = (id: string) => {
    setShowcaseItems((current) => current.filter((item) => item.id !== id));
    setMessage('Firma listeden kaldirildi. Canliya almak icin kaydet.');
  };

  if (!isAuthenticated) {
    return (
      <section className="min-h-screen bg-[#090d14] text-white px-6 py-10 flex items-center justify-center">
        <div className="w-full max-w-md border border-white/10 bg-[#0f1623] p-7 sm:p-9">
          <div className="mb-7">
            <p className="font-mono text-xs tracking-[0.2em] uppercase text-sky-300/70 mb-3">G-Software</p>
            <h1 className="text-3xl font-black tracking-tight">Yönetim Girişi</h1>
            <p className="mt-3 text-sm leading-relaxed text-neutral-500">
              Kontrol paneline erişmek için yönetici tokenini gir.
            </p>
          </div>

          <form
            onSubmit={(event) => {
              event.preventDefault();
              void handleLogin();
            }}
          >
            <label className="block">
              <span className="font-mono text-[11px] text-neutral-500 tracking-widest uppercase">Admin Token</span>
              <input
                type="password"
                value={tokenInput}
                onChange={(event) => setTokenInput(event.target.value)}
                placeholder="MAINTENANCE_ADMIN_TOKEN"
                autoComplete="current-password"
                autoFocus
                className="mt-2 w-full bg-[#0b111c] border border-white/15 px-4 py-3 text-sm text-white placeholder:text-neutral-600 focus:outline-none focus:border-sky-300/60"
              />
            </label>

            <button
              type="submit"
              disabled={authLoading || !tokenInput.trim()}
              className="mt-5 w-full px-6 py-4 bg-white text-black font-mono text-sm tracking-widest uppercase hover:bg-neutral-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
            >
              {authLoading ? 'Doğrulanıyor...' : 'Panele Giriş Yap'}
            </button>
          </form>

          {error && <p className="mt-4 text-sm text-red-300">{error}</p>}
        </div>
      </section>
    );
  }

  return (
    <section className="min-h-screen bg-[#090d14] text-white px-6 py-10">
      <div className="mx-auto max-w-6xl">
        <div className="mb-8 flex flex-col md:flex-row md:items-start md:justify-between gap-5">
          <div>
            <p className="font-mono text-xs tracking-[0.2em] uppercase text-sky-300/70 mb-3">G-Software</p>
            <h1 className="text-3xl md:text-4xl font-black tracking-tight mb-3">Kontrol Paneli</h1>
            <p className="text-sm text-neutral-400 leading-relaxed max-w-2xl">
              Bakım modunu, ürün ilerleme barlarını, indirme istatistiklerini ve web sitesi referanslarını tek yerden yönet.
            </p>
          </div>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={() => void refreshAll(activeToken)}
              disabled={busy}
              className="px-5 py-3 border border-white/20 text-white/80 font-mono text-xs tracking-widest uppercase disabled:opacity-50 disabled:cursor-not-allowed hover:border-white/50 transition-colors cursor-pointer"
            >
              Yenile
            </button>
            <button
              type="button"
              onClick={handleLogout}
              className="px-5 py-3 border border-red-300/25 text-red-200 font-mono text-xs tracking-widest uppercase hover:bg-red-400/10 transition-colors cursor-pointer"
            >
              Çıkış Yap
            </button>
          </div>
        </div>

        <div className="grid lg:grid-cols-[0.8fr_1.2fr] gap-6">
          <div className="border border-white/10 bg-[#0f1623] p-6">
            <div className="border border-white/10 bg-[#0a0f18] px-5 py-4">
              <p className="font-mono text-[11px] tracking-widest uppercase text-neutral-500 mb-2">Bakim Durumu</p>
              <p className={`text-2xl font-black ${maintenance ? 'text-amber-300' : 'text-emerald-300'}`}>
                {maintenance == null ? 'BILINMIYOR' : maintenance ? 'BAKIM ACIK' : 'BAKIM KAPALI'}
              </p>
            </div>

            <button
              type="button"
              onClick={handleToggle}
              disabled={maintenanceLoading || maintenance == null || !hasToken}
              className="mt-5 w-full px-6 py-4 bg-white text-black font-mono text-sm tracking-widest uppercase hover:bg-neutral-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
            >
              {maintenanceLoading ? 'Isleniyor...' : maintenance ? 'Bakım Modunu Kapat' : 'Bakım Modunu Aç'}
            </button>
          </div>

          <div className="border border-white/10 bg-[#0f1623] p-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
              <div>
                <p className="font-mono text-[11px] tracking-widest uppercase text-neutral-500 mb-2">Ürün İlerlemeleri</p>
                <h2 className="text-2xl font-black tracking-tight">İlerleme barları</h2>
              </div>
              <button
                type="button"
                onClick={saveProgress}
                disabled={!hasToken || progressLoading}
                className="px-5 py-3 bg-white text-black font-mono text-xs tracking-widest uppercase hover:bg-neutral-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
              >
                {progressLoading ? 'Kaydediliyor...' : 'Değerleri Kaydet'}
              </button>
            </div>

            <div className="space-y-4">
              {PRODUCT_PROGRESS_KEYS.map((key) => {
                const product = PRODUCT_LABELS[key];
                const value = progress[key];

                return (
                  <div key={key} className="border border-white/10 bg-[#0a0f18] px-5 py-4">
                    <div className="flex items-start justify-between gap-4 mb-4">
                      <div>
                        <p className="font-black text-white">{product.name}</p>
                        <p className="text-xs text-neutral-500 mt-1">{product.meta}</p>
                      </div>
                      <div className="font-mono text-2xl font-black text-sky-200 tabular-nums">{value}%</div>
                    </div>

                    <div className="grid sm:grid-cols-[1fr_90px] gap-3 items-center">
                      <input
                        type="range"
                        min="0"
                        max="100"
                        value={value}
                        onChange={(event) => handleProgressChange(key, Number(event.target.value))}
                        className="w-full accent-sky-300"
                      />
                      <input
                        type="number"
                        min="0"
                        max="100"
                        value={value}
                        onChange={(event) => handleProgressChange(key, Number(event.target.value))}
                        className="w-full bg-[#090d14] border border-white/15 px-3 py-2 text-sm text-white focus:outline-none focus:border-sky-300/60"
                      />
                    </div>

                    <div className="mt-4 h-1.5 bg-white/8 overflow-hidden">
                      <div className="h-full bg-sky-300 transition-all duration-200" style={{ width: `${value}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <div className="mt-6 border border-white/10 bg-[#0f1623] p-6">
          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 mb-6">
            <div>
              <p className="font-mono text-[11px] tracking-widest uppercase text-neutral-500 mb-2">Özel Gün Logosu</p>
              <h2 className="text-2xl font-black tracking-tight">Otomatik veya manuel logo seçimi</h2>
              <p className="mt-2 text-sm text-neutral-500 max-w-3xl">
                Otomatik mod takvimi kullanır. Manuel seçim sadece seçildiği Türkiye günü boyunca geçerlidir ve gece 00:00’da kendiliğinden otomatik moda döner.
              </p>
            </div>
            <a
              href="/"
              target="_blank"
              rel="noopener noreferrer"
              className="shrink-0 px-5 py-3 border border-white/20 text-white/80 font-mono text-xs tracking-widest uppercase hover:border-white/50 transition-colors"
            >
              Siteyi Aç
            </a>
          </div>

          <div className="mb-5 border border-white/10 bg-[#0a0f18] px-5 py-4">
            <p className="font-mono text-[10px] tracking-widest uppercase text-neutral-500 mb-2">Aktif Mod</p>
            <p className={`text-lg font-black ${brandLogo.mode === 'manual' ? 'text-amber-300' : 'text-emerald-300'}`}>
              {brandLogo.mode === 'manual'
                ? `MANUEL — ${SPECIAL_DAY_LOGO_OPTIONS.find((item) => item.key === brandLogo.logoKey)?.label ?? 'Özel Gün'}`
                : 'OTOMATİK TAKVİM'}
            </p>
            {brandLogo.activeDate && (
              <p className="mt-1 text-xs text-neutral-500">Geçerli Türkiye tarihi: {brandLogo.activeDate}</p>
            )}
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            <button
              type="button"
              onClick={() => void updateBrandLogo(null)}
              disabled={!hasToken || brandLogoLoading}
              className={`min-h-28 border p-4 text-left transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer ${
                brandLogo.mode === 'automatic'
                  ? 'border-emerald-300/60 bg-emerald-400/10'
                  : 'border-white/10 bg-[#0a0f18] hover:border-white/30'
              }`}
            >
              <span className="block font-black text-white">Otomatik</span>
              <span className="mt-2 block text-xs leading-relaxed text-neutral-500">Takvimdeki özel günlerde doğru logo kendiliğinden seçilir.</span>
            </button>

            {SPECIAL_DAY_LOGO_OPTIONS.map((option) => {
              const selected = brandLogo.mode === 'manual' && brandLogo.logoKey === option.key;

              return (
                <button
                  key={option.key}
                  type="button"
                  onClick={() => void updateBrandLogo(option.key)}
                  disabled={!hasToken || brandLogoLoading}
                  className={`min-h-28 border p-4 flex items-center gap-4 text-left transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer ${
                    selected
                      ? 'border-amber-300/60 bg-amber-400/10'
                      : 'border-white/10 bg-[#0a0f18] hover:border-white/30'
                  }`}
                >
                  <span className="w-16 h-16 shrink-0 border border-white/10 bg-black/30 flex items-center justify-center">
                    <img src={option.logoUrl} alt="" className="w-14 h-14 object-contain" />
                  </span>
                  <span>
                    <span className="block font-black text-white">{option.label}</span>
                    <span className="mt-1 block font-mono text-[10px] tracking-wider uppercase text-neutral-500">
                      {selected ? 'Canlıda aktif' : 'Manuel seç'}
                    </span>
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        <div className="mt-6 border border-white/10 bg-[#0f1623] p-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
            <div>
              <p className="font-mono text-[11px] tracking-widest uppercase text-neutral-500 mb-2">Ürün İstatistikleri</p>
              <h2 className="text-2xl font-black tracking-tight">Indirme sayilari ve puanlar</h2>
              <p className="mt-2 text-sm text-neutral-500 max-w-2xl">
                Buradaki indirme degeri ana sayfadaki sayaçta ve ilgili urun kartlarinda kullanilir. Puan alanina ister 0, ister 4.8 gibi deger yazabilirsin.
              </p>
            </div>
            <button
              type="button"
              onClick={saveStats}
              disabled={!hasToken || statsLoading}
              className="px-5 py-3 bg-white text-black font-mono text-xs tracking-widest uppercase hover:bg-neutral-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
            >
              {statsLoading ? 'Kaydediliyor...' : 'İstatistikleri Kaydet'}
            </button>
          </div>

          <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
            {PRODUCT_STATS_KEYS.map((key) => {
              const product = PRODUCT_LABELS[key];
              const value = stats[key];

              return (
                <div key={key} className="border border-white/10 bg-[#0a0f18] px-5 py-4">
                  <div className="mb-4">
                    <p className="font-black text-white">{product.name}</p>
                    <p className="text-xs text-neutral-500 mt-1">{product.meta}</p>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-3">
                    <label className="block">
                      <span className="font-mono text-[10px] text-neutral-500 tracking-widest uppercase">Indirme</span>
                      <input
                        type="number"
                        min="0"
                        max="999999999"
                        value={value.downloads}
                        onChange={(event) => handleStatsChange(key, { downloads: clampDownloads(event.target.value) })}
                        className="mt-2 w-full bg-[#090d14] border border-white/15 px-3 py-2 text-sm text-white focus:outline-none focus:border-sky-300/60"
                      />
                    </label>

                    <label className="block">
                      <span className="font-mono text-[10px] text-neutral-500 tracking-widest uppercase">Ort. Puan</span>
                      <input
                        type="text"
                        value={value.rating}
                        onChange={(event) => handleStatsChange(key, { rating: normalizeRating(event.target.value) })}
                        placeholder="0 veya 4.8"
                        className="mt-2 w-full bg-[#090d14] border border-white/15 px-3 py-2 text-sm text-white focus:outline-none focus:border-sky-300/60"
                      />
                    </label>
                  </div>

                  <label className="mt-4 flex items-center justify-between gap-4 border border-white/10 bg-[#090d14] px-4 py-3 cursor-pointer">
                    <span>
                      <span className="block font-mono text-[10px] text-neutral-400 tracking-widest uppercase">Apps kartinda indirme goster</span>
                      <span className="mt-1 block text-xs text-neutral-600">Pasif yaparsan /apps kartindaki indirme kutusu gizlenir.</span>
                    </span>
                    <input
                      type="checkbox"
                      checked={value.showDownloadsInApps}
                      onChange={(event) => handleStatsChange(key, { showDownloadsInApps: event.target.checked })}
                      className="h-5 w-5 accent-sky-300"
                    />
                  </label>

                  <div className="mt-4 grid grid-cols-2 gap-px bg-white/10">
                    <div className="bg-[#101826] px-4 py-3">
                      <p className="font-mono text-lg font-black text-sky-200 tabular-nums">{value.downloads.toLocaleString()}+</p>
                      <p className="font-mono text-[10px] text-neutral-500 tracking-widest uppercase mt-1">
                        {value.showDownloadsInApps ? 'Apps kartinda aktif' : 'Apps kartinda gizli'}
                      </p>
                    </div>
                    <div className="bg-[#101826] px-4 py-3">
                      <p className="font-mono text-lg font-black text-sky-200 tabular-nums">{value.rating}</p>
                      <p className="font-mono text-[10px] text-neutral-500 tracking-widest uppercase mt-1">Puan</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="mt-6 border border-white/10 bg-[#0f1623] p-6">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4 mb-6">
            <div>
              <p className="font-mono text-[11px] tracking-widest uppercase text-neutral-500 mb-2">Web Sitesi Referansları</p>
              <h2 className="text-2xl font-black tracking-tight">Teslim edilen siteler</h2>
              <p className="mt-2 text-sm text-neutral-500 max-w-2xl">
                Buraya eklenen firma, logo ve site linkleri web sitesi teklif sayfasında referans olarak gösteriliri.
              </p>
            </div>
            <button
              type="button"
              onClick={saveShowcase}
              disabled={!hasToken || showcaseLoading}
              className="px-5 py-3 bg-white text-black font-mono text-xs tracking-widest uppercase hover:bg-neutral-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
            >
              {showcaseLoading ? 'Kaydediliyor...' : 'Referanslari Kaydet'}
            </button>
          </div>

          <div className="grid lg:grid-cols-[1fr_1.2fr_1.2fr_auto] gap-3">
            <label className="block">
              <span className="font-mono text-[10px] text-neutral-500 tracking-widest uppercase">Firma Adi</span>
              <input
                type="text"
                value={newCompanyName}
                onChange={(event) => setNewCompanyName(event.target.value)}
                placeholder="Ornek Firma"
                className="mt-2 w-full bg-[#0a0f18] border border-white/15 px-4 py-3 text-sm text-white placeholder:text-neutral-600 focus:outline-none focus:border-sky-300/60"
              />
            </label>

            <label className="block">
              <span className="font-mono text-[10px] text-neutral-500 tracking-widest uppercase">Logo URL veya Site Ici Yol</span>
              <input
                type="text"
                value={newLogoUrl}
                onChange={(event) => setNewLogoUrl(event.target.value)}
                placeholder="/images/ornek-logo.png veya https://..."
                className="mt-2 w-full bg-[#0a0f18] border border-white/15 px-4 py-3 text-sm text-white placeholder:text-neutral-600 focus:outline-none focus:border-sky-300/60"
              />
            </label>

            <label className="block">
              <span className="font-mono text-[10px] text-neutral-500 tracking-widest uppercase">Site Linki</span>
              <input
                type="url"
                value={newSiteUrl}
                onChange={(event) => setNewSiteUrl(event.target.value)}
                placeholder="https://ornekfirma.com"
                className="mt-2 w-full bg-[#0a0f18] border border-white/15 px-4 py-3 text-sm text-white placeholder:text-neutral-600 focus:outline-none focus:border-sky-300/60"
              />
            </label>

            <button
              type="button"
              onClick={handleAddShowcaseItem}
              className="self-end px-5 py-3 border border-sky-300/35 text-sky-100 font-mono text-xs tracking-widest uppercase hover:bg-sky-400/10 transition-colors cursor-pointer"
            >
              Ekle
            </button>
          </div>

          <div className="mt-6 space-y-3">
            {showcaseItems.length > 0 ? (
              showcaseItems.map((item) => (
                <div key={item.id} className="grid lg:grid-cols-[72px_1fr_1.2fr_1.2fr_auto] gap-3 items-center border border-white/10 bg-[#0a0f18] p-4">
                  <div className="w-16 h-16 border border-white/10 bg-black/30 flex items-center justify-center overflow-hidden">
                    <img src={item.logoUrl} alt="" className="w-12 h-12 object-contain" />
                  </div>
                  <input
                    type="text"
                    value={item.name}
                    onChange={(event) => handleUpdateShowcaseItem(item.id, { name: event.target.value })}
                    className="w-full bg-[#090d14] border border-white/15 px-3 py-2 text-sm text-white focus:outline-none focus:border-sky-300/60"
                  />
                  <input
                    type="text"
                    value={item.logoUrl}
                    onChange={(event) => handleUpdateShowcaseItem(item.id, { logoUrl: event.target.value })}
                    className="w-full bg-[#090d14] border border-white/15 px-3 py-2 text-sm text-white focus:outline-none focus:border-sky-300/60"
                  />
                  <input
                    type="url"
                    value={item.siteUrl}
                    onChange={(event) => handleUpdateShowcaseItem(item.id, { siteUrl: event.target.value })}
                    className="w-full bg-[#090d14] border border-white/15 px-3 py-2 text-sm text-white focus:outline-none focus:border-sky-300/60"
                  />
                  <button
                    type="button"
                    onClick={() => handleRemoveShowcaseItem(item.id)}
                    className="px-4 py-3 border border-red-300/25 text-red-200 font-mono text-xs tracking-widest uppercase hover:bg-red-400/10 transition-colors cursor-pointer"
                  >
                    Sil
                  </button>
                </div>
              ))
            ) : (
              <div className="border border-white/10 bg-[#0a0f18] px-5 py-6">
                <p className="text-sm text-neutral-500">
                  Henuz referans eklenmedi. Firma adini, logo yolunu ve site linkini girip Ekle, sonra Referanslari Kaydet butonuna bas.
                </p>
              </div>
            )}
          </div>
        </div>

        {message && <p className="mt-5 text-sm text-emerald-300">{message}</p>}
        {error && <p className="mt-3 text-sm text-red-300">{error}</p>}
      </div>
    </section>
  );
}
